import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { trackClick } from '@hotjar/tracking';

import { OptionCard, type OptionCardProps } from '../OptionCard/OptionCard';

import { SMART_PANEL_SPACING } from '../../constants-types/Layout';
import { HEATMAP_DEVICE_TYPES } from '../../constants-types/HeatmapDeviceTypes';
import type { HighlightsProps, TrackableSite } from '../../constants-types/ExternalDependencyTypes';
import { SmartPanelSection, SmartPanelSegment } from './SmartPanelBlocks';
import { HEATMAP_DATA_TYPES } from '../../constants-types/HeatmapDataTypes';
import { EVENT_NAMES } from '../../constants-types/TrackEventNames';
import { EVENT_UI_POSITIONS } from '../../constants-types/TrackEventUIPositions';
import { getTrackGroupDataAndTriggeredOnState } from '../../utils/trackingUtils';

import top3ClickedIllustration from '../../icons/top-3-clicks.svg';
import allClicksIllustration from '../../icons/all-clicks.svg';
import highlightsIllustration from '../../icons/highlights.svg';
import engagementZonesIllustration from '../../icons/engagement-zones.svg';

const allClicksClassName = 'hj-hm-sp-layers-all-clicks';
const top3ClicksClassName = 'hj-hm-sp-layers-top-3-clicks';
const highlightsClassName = 'hj-hm-sp-layers-highlights';
const engagementZonesClassName = 'hj-hm-sp-layers-engagement-zones';

function trackLayerToggled(layerName: string, isOn: boolean, currentSite?: TrackableSite): void {
  if (currentSite !== undefined) {
    const name = `${layerName} ${isOn ? EVENT_NAMES.TOGGLED_ON : EVENT_NAMES.TOGGLED_OFF}`;
    trackClick({
      name,
      uiPosition: EVENT_UI_POSITIONS.SMART_PANEL,
      ...getTrackGroupDataAndTriggeredOnState(currentSite),
    });
  }
}

function trackInfoIconClicked(currentSite?: TrackableSite): void {
  if (currentSite !== undefined) {
    trackClick({
      name: EVENT_NAMES.ENGAGEMENT_ZONES_MORE_INSIGHTS,
      uiPosition: EVENT_UI_POSITIONS.SMART_PANEL,
      ...getTrackGroupDataAndTriggeredOnState(currentSite),
    });
  }
}

const StyledSmartPanelSegment = styled(SmartPanelSegment)`
  display: grid;
  grid-gap: ${SMART_PANEL_SPACING};
  grid-template-columns: 1fr 1fr;
`;

const MultiSelectOptionCard = ({
  isChecked,
  onChange,
  name,
  hasInfoIcon,
  onInfoIconClick,
  infoIconTooltipText,
  isLoading,
  isDisabled,
  disabledTooltipText,
  ...props
}: Pick<
  OptionCardProps,
  | 'title'
  | 'isChecked'
  | 'isDisabled'
  | 'onChange'
  | 'name'
  | 'illustration'
  | 'hasInfoIcon'
  | 'onInfoIconClick'
  | 'infoIconTooltipText'
  | 'isLoading'
  | 'className'
  | 'disabledTooltipText'
>): JSX.Element => {
  return (
    <OptionCard
      {...props}
      isChecked={isChecked}
      onChange={onChange}
      name={name}
      hasInfoIcon={hasInfoIcon}
      onInfoIconClick={onInfoIconClick}
      infoIconTooltipText={infoIconTooltipText}
      isLoading={isLoading}
      isDisabled={isDisabled}
      disabledTooltipText={disabledTooltipText}
    />
  );
};

export function LayersSection({
  device,
  heatType,
  isHeatLayerActive,
  onHeatLayerToggle,
  isTopClicksWidgetActive,
  onTopClicksWidgetToggle,
  highlights,
  currentSite,
  isEngagementZonesActive,
  onEngagementZonesToggle,
  onEngagementInfoIconClick,
  isEngagementZonesAvailable,
  isFetchingEngagementData,
  isEngagementZonesDisabled,
  engagementZonesDisabledTooltipText,
}: {
  device: HeatmapDeviceTypes;
  heatType: HeatmapDataType;
  isHeatLayerActive: boolean;
  onHeatLayerToggle: (isActive: boolean) => void;
  isTopClicksWidgetActive: boolean;
  onTopClicksWidgetToggle: (isActive: boolean) => void;
  highlights?: HighlightsProps;
  currentSite?: TrackableSite;
  isEngagementZonesActive: boolean;
  onEngagementZonesToggle: (isActive: boolean) => void;
  onEngagementInfoIconClick: () => void;
  isEngagementZonesAvailable: boolean;
  isFetchingEngagementData: boolean;
  isEngagementZonesDisabled?: boolean;
  engagementZonesDisabledTooltipText?: string;
}): JSX.Element | null {
  const { t } = useTranslation('heatmaps');
  const topClicksOrTaps =
    device === HEATMAP_DEVICE_TYPES.DESKTOP
      ? t('smart-panel.top-clicks')
      : t('smart-panel.top-taps');
  const allClicksOrTaps =
    device === HEATMAP_DEVICE_TYPES.DESKTOP
      ? t('smart-panel.all_clicks')
      : t('smart-panel.all_taps');
  return (
    <SmartPanelSection>
      <StyledSmartPanelSegment>
        {heatType === HEATMAP_DATA_TYPES.CLICK && (
          <>
            <MultiSelectOptionCard
              name="all-clicks-taps"
              title={allClicksOrTaps}
              illustration={allClicksIllustration}
              isChecked={isHeatLayerActive}
              onChange={() => {
                onHeatLayerToggle(!isHeatLayerActive);
                trackLayerToggled('All Clicks', !isHeatLayerActive, currentSite);
              }}
              className={allClicksClassName}
            />
            <MultiSelectOptionCard
              name="top-clicks-taps"
              title={topClicksOrTaps}
              illustration={top3ClickedIllustration}
              isChecked={isTopClicksWidgetActive}
              onChange={() => {
                onTopClicksWidgetToggle(!isTopClicksWidgetActive);
                trackLayerToggled('Top 3 Clicks', !isTopClicksWidgetActive, currentSite);
              }}
              className={top3ClicksClassName}
            />
          </>
        )}
        {isEngagementZonesAvailable && (
          <MultiSelectOptionCard
            name="top-clicks-taps"
            title={t('smart-panel.engagement-zones.title')}
            illustration={engagementZonesIllustration}
            isChecked={isEngagementZonesActive}
            onChange={() => {
              onEngagementZonesToggle(!isEngagementZonesActive);
              trackLayerToggled('Engagement Zones', !isEngagementZonesActive, currentSite);
            }}
            hasInfoIcon
            onInfoIconClick={() => {
              onEngagementInfoIconClick();
              trackInfoIconClicked(currentSite);
            }}
            infoIconTooltipText={t('smart-panel.engagement-zones.info-tooltip')}
            isLoading={isFetchingEngagementData}
            className={engagementZonesClassName}
            isDisabled={isEngagementZonesDisabled ?? false}
            disabledTooltipText={engagementZonesDisabledTooltipText}
          />
        )}
        <MultiSelectOptionCard
          name="highlights"
          title={t('smart-panel.highlights')}
          illustration={highlightsIllustration}
          isChecked={highlights?.isActive}
          onChange={() => {
            if (highlights !== undefined) {
              if (highlights.isActive) highlights.deactivate();
              if (!highlights.isActive) highlights.activate();
              trackLayerToggled('Highlights', !highlights.isActive, currentSite);
            }
          }}
          className={highlightsClassName}
        />
      </StyledSmartPanelSegment>
    </SmartPanelSection>
  );
}
